// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useNavigationContext } from '@/contexts/navigation-context';
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

interface NavigationSubsectionItem {
  label: string;
  to: string;
  icon: ReactNode;
  disabled?: boolean;
  navbarChip?: string;
}

interface NavigationSubsectionBarProps {
  items: NavigationSubsectionItem[];
  activeSectionRef?: { current: HTMLElement | null };
  alignment: 'left' | 'center' | 'right';
}

/**
 * A horizontal bar displaying subsection navigation items with icons and labels.
 * Shows below the main navigation when a section is active.
 *
 * Alignment behavior:
 * - 'left': Aligns the first subsection item with the left edge of the active main navigation section
 * - 'right': Aligns the last subsection item with the right edge of the active main navigation section
 */
export function NavigationSubsectionBar(props: NavigationSubsectionBarProps) {
  const theme = useTheme();
  const { activeSubsection, setActiveSubsection } = useNavigationContext();
  const [leftOffset, setLeftOffset] = useState(0);
  const stackRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate actual position of active section using DOM measurement
  useLayoutEffect(() => {
    const calculateOffset = () => {
      if (!props.activeSectionRef?.current) {
        setLeftOffset(0);
        return;
      }

      const activeSectionElement = props.activeSectionRef.current;

      // Find the NavigationLink button and target its left edge
      const navigationButton = activeSectionElement.querySelector('button');
      const targetElement = navigationButton || activeSectionElement;

      const rect = targetElement.getBoundingClientRect();

      // Get the container's position to calculate relative offset
      const container = containerRef.current;

      if (container) {
        const containerRect = container.getBoundingClientRect();

        // Get the container's computed styles to find its padding
        const containerStyles = window.getComputedStyle(container);
        const containerPaddingLeft = parseFloat(containerStyles.paddingLeft);

        if (props.alignment === 'left') {
          // Left alignment: align first subsection item with left edge of main nav button
          const relativeOffset =
            rect.left - (containerRect.left + containerPaddingLeft);
          setLeftOffset(relativeOffset);
        } else {
          // Right alignment: align last subsection item with right edge of main nav button
          // Find the actual content width by measuring from first to last button
          if (stackRef.current) {
            const buttons = stackRef.current.querySelectorAll(':scope > *');
            if (buttons.length > 0) {
              const firstButton = buttons[0];
              const lastButton = buttons[buttons.length - 1];
              const firstButtonRect = firstButton.getBoundingClientRect();
              const lastButtonRect = lastButton.getBoundingClientRect();

              // Content width = from left edge of first button to right edge of last button
              const contentWidth = lastButtonRect.right - firstButtonRect.left;

              let rightEdgeOffset = 0;
              if (props.alignment === 'center') {
                rightEdgeOffset =
                  rect.right -
                  (rect.right - rect.left) / 2 -
                  containerPaddingLeft -
                  contentWidth / 2;
              } else {
                rightEdgeOffset =
                  rect.right - containerPaddingLeft - contentWidth;
              }

              setLeftOffset(Math.max(0, rightEdgeOffset)); // Ensure we don't go negative
            } else {
              setLeftOffset(0);
            }
          } else {
            // Fallback if stackRef not available yet
            setLeftOffset(0);
          }
        }
      } else {
        // Fallback: use absolute position from viewport
        setLeftOffset(rect.left);
      }
    };

    // Calculate initial offset
    calculateOffset();

    // Add resize listener to recalculate on window resize
    window.addEventListener('resize', calculateOffset);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('resize', calculateOffset);
    };
  }, [props.activeSectionRef, props.alignment, props.items]);

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        backgroundColor: theme.palette.grey[50],
        minHeight: '48px', // Ensure consistent height even when empty
      }}
    >
      <Container ref={containerRef} maxWidth={false}>
        <Stack
          ref={stackRef}
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            paddingLeft: `${leftOffset}px`,
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {props.items &&
            props.items.length > 0 &&
            props.items.map((item) => {
              const isActive = activeSubsection === item.label;
              const isDisabled = item.disabled || false;

              return (
                <Button
                  key={item.to}
                  component={isDisabled ? 'div' : Link}
                  to={isDisabled ? undefined : item.to}
                  onClick={
                    isDisabled
                      ? undefined
                      : () => {
                          setActiveSubsection(item.label);
                        }
                  }
                  disabled={isDisabled}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: theme.spacing(1.5, 3),
                    textDecoration: 'none',
                    borderTopWidth: '2px',
                    borderTopStyle: 'solid',
                    borderTopColor: theme.palette.primary.main,
                    borderRadius: 0,
                    color: isDisabled
                      ? theme.palette.text.disabled
                      : isActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    backgroundColor:
                      isActive && !isDisabled
                        ? theme.palette.primary.light + '10'
                        : 'transparent',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    cursor: isDisabled ? 'default' : 'pointer',
                    '& .MuiButton-endIcon': {
                      color: isDisabled
                        ? theme.palette.text.disabled
                        : isActive
                          ? theme.palette.primary.main
                          : 'inherit',
                    },
                    '&:hover': isDisabled
                      ? {}
                      : {
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.primary.light + '10',
                        },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: isDisabled
                        ? theme.palette.text.disabled
                        : 'inherit',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={isActive && !isDisabled ? 600 : 400}
                    color={isDisabled ? 'text.disabled' : 'inherit'}
                  >
                    {item.label}
                  </Typography>
                  {item.navbarChip && (
                    <Chip
                      label={item.navbarChip}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: '20px',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.divider,
                        backgroundColor: 'transparent',
                      }}
                    />
                  )}
                </Button>
              );
            })}
        </Stack>
      </Container>
    </Box>
  );
}
